import { useToast } from "uiShadcn/hooks/use-toast";
import { zodResolver } from "@hookform/resolvers/zod";
import * as Sentry from "@sentry/nextjs";
import type { ISendFormResponseError } from "api/sendForm";
import { updateForm } from "api/updateForm";
import { extractJsonSchemaSteps, extractStepsElements } from "forms/helpers/stepsHelpers";
import { useFormErrors } from "forms/hooks/useFormErrors";
import useParseSchema from "forms/hooks/useParseSchema";
import { useParams, useRouter, useSearchParams } from "next/navigation";
import { useTransition } from "react";
import { useForm } from "react-hook-form";
import { useIntl } from "react-intl";
import type { IJsonSchema } from "types/jsonSchema";
import type { IUiSchema } from "types/uiSchema";
import { isFetchErrorBody } from "utils/helpers/fetchError";
import { logger } from "utils/helpers/logger";
import { checkIfPracujMobileApplicationSucceedExists, onPracujMobileApplicationSucceed } from "utils/helpers/pracujMobile";
import { removeKeysByValue } from "utils/helpers/removeKeysByValue";
import { z } from "zod";
import { getRedirectToThankYouPageUrl, isThankYouPageUrlValid } from "../firstStep/firstStepForm/redirectFunction";
interface UseSecondStepFormProps {
  jsonSchema: IJsonSchema;
  uiSchema: IUiSchema;
}
export default function useSecondStepForm({
  jsonSchema,
  uiSchema
}: UseSecondStepFormProps) {
  const schema = z.looseObject({});
  const {
    toast
  } = useToast();
  const {
    formatMessage
  } = useIntl();
  const [isSubmitting, startTransition] = useTransition();
  const uiSteps = extractStepsElements(uiSchema);
  const jsonSteps = extractJsonSchemaSteps(jsonSchema);
  const {
    displayError
  } = useFormErrors();
  const params = useParams<{
    formId: string;
  }>();
  const router = useRouter();
  const searchParams = useSearchParams();
  const {
    parseSchema
  } = useParseSchema();
  const parsedSchema = parseSchema(jsonSteps.secondStep, jsonSchema?.required);
  const form = useForm<z.infer<typeof schema>>({
    resolver: zodResolver(parsedSchema ?? schema),
    defaultValues: {}
  });
  const onSubmitHandler = async (data: Record<string, unknown>) => {
    startTransition(async () => {
      try {
        const applyId_0 = searchParams.get("applyId");
        const id_0 = params.formId;
        if (!applyId_0 || !id_0) {
          return;
        }
        const nullRemoved = removeKeysByValue(data, val => val === null);
        logger.log("onSubmitHandler", "Form submitted with", nullRemoved);
        const resp = await updateForm({
          id: id_0,
          applyId: applyId_0,
          data: nullRemoved,
          applyStep: 2
        });
        if (resp.isSuccess) {
          const isExternalThankYouPageUrlExist = isThankYouPageUrlValid(searchParams);
          const thankYouUrl = isExternalThankYouPageUrlExist ? getRedirectToThankYouPageUrl() : `/form/${id_0}/thank-you?${searchParams.toString()}`;
          if (checkIfPracujMobileApplicationSucceedExists()) {
            onPracujMobileApplicationSucceed(searchParams.get("withCv") === "true");
          }
          router.replace(thankYouUrl);
        }
        if (!resp.isSuccess && isFetchErrorBody<ISendFormResponseError>(resp.error)) {
          const {
            code,
            details
          } = resp.error.data;
          if (code === "IncorrectInputData") {
            details.errors.forEach(e_0 => {
              displayError({
                obj: data,
                setError: form.setError,
                e: e_0
              });
            });
          }
        }
      } catch (e) {
        toast({
          title: formatMessage({
            id: "something_went_wrong"
          }),
          variant: "destructive"
        });
        logger.error(e);
        const applyId = searchParams.get("applyId");
        const id = params.formId;
        Sentry.withScope(scope => {
          scope.setTag("feature", "submit-second-step");
          scope.setContext("Form data", {
            formId: id,
            applyId
          });
          Sentry.captureException(e);
        });
      }
    });
  };
  return {
    isSubmitting,
    form,
    onSubmitHandler,
    uiSteps
  };
}